<template>
  <div
    :id="task.id"
    ref="taskRef"
    v-clickOutside="cancelEdit"
    class="group relative flex flex-col justify-between rounded-8 h-full w-full mb-12 bg-primary duration-300 ease-in-out"
    :class="[
      isDarkMode ?
        `border-2 border-grey-500 md:hover:border-grey-300 transition-border ${isEdit ? '' : 'md:hover:bg-sidebar'}` :
        `shadow-event md:hover:shadow-event-hover transition-shadow ${isEdit ? '' : 'md:hover:bg-grey-450'}`,
      isEdit ? 'cursor-default no-drag' : 'cursor-grab drag-handler'
    ]"
    @click="openModal"
  >
    <div v-if="!isEdit" class="no-drag flex items-center absolute top-0 right-0 px-10 pb-10 pt-5 pr-12 z-20">
      <div class="md:opacity-0 group-hover:opacity-100 anim-opacity">
        <ButtonModule
          prefix="icon-check-circle-outline text-16"
          :icon-has-margin="false"
          has-hover
          class="rounded-6 anim-color-200"
          :class="[
            isMagenta ? 'text-dark-600' : 'text-app-color',
            isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover',
          ]"
          @click="completeTask"
        />
      </div>
      <DropdownModule
        v-if="!isCompleted"
        ref="menuRef"
        icon="icon-more-horizontal text-20"
        :width="150"
        :top-position="30"
        append-to-body
        class="md:opacity-0 group-hover:opacity-100 anim-opacity"
        :class="{'opacity-100': showMenu}"
        remove-optional-top-gap
        :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
        is-auto-menu-position
        @click="showMenu = true"
        @close="showMenu = false"
      >
        <div>
          <div class="hover:bg-selection rounded-t-4 group-scope text-15">
            <ButtonModule
              text="Edit"
              :prefix="`icon-edit mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="p-10 w-full"
              @click="toggleEdit"
            />
          </div>
          <div class="hover:bg-selection group-scope text-15">
            <ButtonModule
              text="Delete"
              :prefix="`icon-delete mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="p-10 w-full"
              @click="deleteTask"
            />
          </div>
        </div>
      </DropdownModule>
    </div>

    <EmojiPicker
      v-if="showEmojiPicker"
      ref="emojiPickerRef"
      v-clickOutside="closeEmojiPicker"
      class="top-80 left-0"
      :class="{ '-top-280': isMobile }"
      @selectEmoji="insertEmoji"
      @click.stop
    />
    <div class="p-14" :class="[isEdit ? 'cursor-default' : 'cursor-grab drag-handler', {'pb-0': !isCompleted}]">
      <div
        v-if="!isEdit"
        class="font-Inter text-15 leading-small font-semibold pr-25 line-clamp-2 select-none md:select-auto"
        :class="{'line-through opacity-40': isCompleted}"
      >
        {{ localTask.title }}
      </div>
      <input
        v-else
        id="title"
        ref="titleInputRef"
        v-model="localTask.title"
        placeholder="What’s the task?"
        class="font-Inter w-full mb-10 text-15 bg-transparent outline-none font-bold pr-5 leading-small block"
        maxlength="40"
        type="text"
        @blur="onBlur"
        @keypress="onKeyPress"
        @keypress.enter="cancelEdit"
      >

      <p
        v-if="!isEdit && localTask.subTitle"
        class="text-14 leading-normal line-clamp-3 select-none md:select-auto mt-4"
        :class="[isDarkMode ? 'text-grey-250' : 'text-grey-1000', {'line-through opacity-40': isCompleted}]"
      >
        {{ localTask.subTitle }}
      </p>
      <input
        v-if="isEdit"
        id="subTitle"
        ref="subTitleInputRef"
        v-model="localTask.subTitle"
        placeholder="Describe it in more detail."
        class="w-full h-full text-14 bg-transparent outline-none pr-5 -mt-5 block"
        maxlength="100"
        type="text"
        @blur="onBlur"
        @keypress="onKeyPress"
        @keypress.enter="cancelEdit"
      >

      <div v-if="isEdit" class="border-t border-grey-500 mt-5" />

      <div v-if="isEdit" class="flex items-center mt-4">
        <ButtonModule
          text="Urgent"
          class="anim-color px-10 py-4 rounded-8 mt-5 mr-10 text-13"
          :class="task.isUrgent ? 'bg-error text-white' : 'bg-grey-500 text-dark-600'"
          @click="toggleUrgent"
        />

        <Mentions v-model="taskProject" class="pt-5" />
      </div>

      <div v-if="!isEdit && (task.isUrgent || taskProject) && !isCompleted" class="flex items-center text-13 text-brand mt-10">
        <span
          v-if="task.isUrgent"
          class="inline-block anim-color px-10 py-4 rounded-8 mr-10 bg-error text-white leading-tight"
        >
          Urgent
        </span>
        <router-link v-if="taskProject" class="no-drag leading-tight" :to="url">@{{ taskProject.title }}</router-link>
      </div>
    </div>

    <div
      v-if="!isCompleted"
      class="flex justify-end p-14 pt-0"
      :class="isEdit ? 'cursor-default' : 'cursor-grab drag-handler'"
    >
      <div class="flex items-center leading-none">
        <div v-if="additionalInfo.attachments" class="flex item-center opacity-30 mr-7">
          <i class="icon-attach-file text-18" />
          <p>{{ additionalInfo.attachments }}</p>
        </div>

        <div v-if="countMessages > 0" class="flex item-center opacity-30 mr-10">
          <i class="icon-chat mr-3" />
          <p class="leading-none">{{ countMessages }}</p>
        </div>
        <i v-if="additionalInfo.hasContent" class="icon-additional_info text-22 opacity-30" />
      </div>
      <div v-if="!isEdit && hasTags" class="flex items-center -mr-5 mt-3 ml-15 text-dark-600 text-12">
        <div
          v-for="tag in task.taskTags"
          :key="tag.id"
          class="text-11 font-medium flex items-center flex-shrink-0 rounded-8 h-23 min-w-50 px-7 mr-5 lowercase select-none md:select-auto"
          :style="`background-color: #${(tag.color).toString(16)}`"
        >
          <p v-if="!!tag.name" class="text-12">#</p>
          {{ tag.name }}
        </div>
      </div>

      <TagsModal v-if="isEdit" class="mt-3 ml-15" :selected-tags="task.taskTags" :tags="tags" @toggleTag="toggleTaskTag" @updateTag="updateTag" />
    </div>
    <TaskBoardModal v-if="showTaskBoardModal" v-model="additionalInfo" :task="task" @close="closeModal" />
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import TagsModal from '@/components/week-planner/TagsModal'
import Mentions from '@/components/week-planner/Mentions'
import EmojiPicker from '@/components/shared/EmojiPicker'
import TaskBoardModal from '@/components/task-board/TaskBoardModal'

export default defineComponent({
  components: {
    DropdownModule,
    ButtonModule,
    TagsModal,
    Mentions,
    EmojiPicker,
    TaskBoardModal
  },

  props: {
    task: Object
  },

  emits: ['getTasks'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()

    const taskRef = ref(null)
    const titleInputRef = ref(null)
    const subTitleInputRef = ref(null)

    const showTaskBoardModal = ref(false)

    const additionalInfo = ref({})

    const timeWasUpdated = false

    const showEmojiPicker = ref(false)
    const showMenu = ref(false)

    const isMagenta = computed(() => getters.isMagenta)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isMobile = computed(() => getters.isMobile)
    const team = computed(() => state.team.team)
    const tags = computed(() => state.task.tags.filter(tag => tag.bookId === selectedBook.value?.id))
    const books = computed(() => state.book.books.filter(b => !b.isSection))
    const countMessages = computed(() => props.task.chat?.messages?.length || 0)

    const isCompleted = computed(() => !!props.task.completedAt)

    const localTask = computed({
      get: () => props.task,
      set: value => commit('updateTask', value)
    })

    const isEdit = ref(!props.task.title)

    const temporaryTitle = ref(props.task.title || '')
    const temporarySubTitle = ref(props.task.subTitle || '')

    const hasTags = computed(() => props.task.taskTags?.length > 0)

    const selectedBook = computed(() => books.value.find(b => b.id === props.task.bookId))

    const taskProject = computed({
      get: () => {
        return state.project.projects.find(p => p.id === props.task.projectId)
      },
      set: (value) => {
        const project = value ? {
          ...value,
          bookId: value.book.id
        } : null

        dispatch('updateTask', {
          id: props.task.id,
          bookId: props.task.bookId,
          projectId: value?.id || null
        })

        commit('updateTask', {
          ...props.task,
          bookId: props.task.bookId,
          project: project
        })
      }
    })

    const url = computed(() => {
      const path = isTeamAccount.value ? `/${team.value.link}` : ''

      return `${path}/space/${taskProject.value.book.id}?page=${taskProject.value.id}`
    })

    const toggleEdit = (value) => {
      setTimeout(() => {
        isEdit.value = true
        nextTick(_ => {
          titleInputRef.value.focus()
          titleInputRef.value.setSelectionRange(0, 9999)
        })
      }, 100)
    }

    onMounted(async () => {
      checkAdditionalInfo()
    })

    function checkAdditionalInfo () {
      const body = document.createElement('body')

      body.innerHTML = props.task.additionalInfo

      const gifs = props.task.additionalInfo.match(/type="gif"/g)
      const images = props.task.additionalInfo.match(/type="image"/g)
      const files = props.task.additionalInfo.match(/type="file"/g)

      additionalInfo.value = {
        attachments: gifs?.length + images?.length + files?.length,
        hasContent: !!body.textContent
      }
    }

    function deleteTask () {
      dispatch('deleteTask', { id: props.task.id, bookId: props.task.bookId })
    }

    function cancelEdit () {
      isEdit.value = false
      save()
    }

    function onBlur () {
      if (!showEmojiPicker.value) {
        save()
      }
    }

    function save () {
      const title = localTask.value.title.trim()
      const subTitle = localTask.value.subTitle?.trim()

      if (title === '') {
        commit('setNotification', {
          isVisible: true,
          message: 'Title cannot be empty'
        })
        localTask.value.title = temporaryTitle.value
        return
      }

      if (title !== temporaryTitle.value.trim() || subTitle !== temporarySubTitle.value || timeWasUpdated) {
        temporaryTitle.value = localTask.value.title
        temporarySubTitle.value = localTask.value.subTitle

        const data = {
          title: title,
          subTitle: subTitle
        }

        dispatch('updateTask', {
          id: props.task.id,
          bookId: props.task.bookId,
          ...data
        })

        commit('updateTask', {
          ...props.task,
          bookId: props.task.bookId,
          ...data
        })
      }
    }

    function toggleTaskTag ({ tag, isSelected }) {
      dispatch('toggleTaskTag', {
        tag: tag,
        taskId: props.task.id,
        isSelected: isSelected,
        bookId: props.task.bookId
      })
    }

    function toggleUrgent () {
      dispatch('updateTask', {
        id: props.task.id,
        bookId: props.task.bookId,
        isUrgent: !props.task.isUrgent
      })

      commit('updateTask', {
        ...props.task,
        bookId: props.task.bookId,
        isUrgent: !props.task.isUrgent
      })
    }

    function updateTag ({ name, id }) {
      dispatch('updateTaskTag', { name, id, bookId: props.task.bookId })
        .then(() => emit('getTasks'))
    }

    function closeEmojiPicker () {
      showEmojiPicker.value = false
    }

    let focusedInput = ''

    function onKeyPress (e) {
      if (e.keyCode === 58) {
        focusedInput = e.target.id
        showEmojiPicker.value = true
      } else {
        focusedInput = ''
        showEmojiPicker.value = false
      }
    }

    function insertEmoji (emoji) {
      if (focusedInput === 'title') {
        titleInputRef.value.focus()
        if (localTask.value.title.length >= 40) return
      } else {
        subTitleInputRef.value.focus()
        if (localTask.value.subTitle.length >= 100) return
      }

      if (localTask.value[focusedInput].slice(-1) === ':') {
        localTask.value[focusedInput] = localTask.value[focusedInput].slice(0, -1) + emoji.char
      } else {
        localTask.value[focusedInput] += emoji.char
      }
    }

    function completeTask () {
      const completedAt = props.task.completedAt ? null : new Date()

      dispatch('complete', {
        id: props.task.id,
        bookId: props.task.bookId
      })
      commit('updateTask', {
        ...props.task,
        completedAt
      })
    }

    function hasPreventClass (el) {
      if (!el || el.classList.contains('no-drag')) {
        return true
      } else {
        if (el && el.parentElement?.tagName !== 'BODY') {
          return hasPreventClass(el.parentElement)
        } else {
          return false
        }
      }
    }

    function openModal (e) {
      if (!hasPreventClass(e.target)) {
        showTaskBoardModal.value = true
      }
    }

    function closeModal (e) {
      showTaskBoardModal.value = false
    }

    return {
      // refs
      taskRef,
      titleInputRef,
      subTitleInputRef,
      showTaskBoardModal,
      // data
      showMenu,
      localTask,
      additionalInfo,
      showEmojiPicker,
      // computed
      url,
      tags,
      books,
      isEdit,
      taskProject,
      hasTags,
      isMobile,
      isMagenta,
      isDarkMode,
      isCompleted,
      selectedBook,
      countMessages,
      isTwilightTheme,
      // functions
      onBlur,
      toggleTaskTag,
      openModal,
      closeModal,
      updateTag,
      toggleEdit,
      cancelEdit,
      onKeyPress,
      deleteTask,
      insertEmoji,
      toggleUrgent,
      completeTask,
      closeEmojiPicker
    }
  }
})
</script>

<style>
.bar-today::after {
  content: 'Today';
  font-size: 9px;
  position: absolute;
  color: var(--link-color);
  bottom: 0;
  font-weight: 700;
}
</style>
